/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  	providedIn: 'root'
})

export class LanguageService {

	private static readonly DEFAULT_LANG = 'es';
	private static readonly SUPPORTED_LANGS = ['es', 'en', 'eu'];

	private langSubject = new BehaviorSubject<string>(this.resolveInitialLang());
	public lang$ = this.langSubject.asObservable();

	constructor(private translate: TranslateService) {
		this.translate.setDefaultLang(LanguageService.DEFAULT_LANG);
		this.translate.use(this.langSubject.value);
	}

	public changeLang(lang: string): void {
		const normalizedLang = this.normalizeLang(lang);
		this.translate.use(normalizedLang);
		localStorage.setItem('language', normalizedLang);
		this.langSubject.next(normalizedLang);
	}

	public getCurrentLang(): string {
		return this.langSubject.value;
	}

	private resolveInitialLang(): string {
		const storedLang = localStorage.getItem('language');
		if (storedLang) {
			return this.normalizeLang(storedLang);
		}

		return this.normalizeLang(navigator.language);
	}

	private normalizeLang(lang: string | undefined | null): string {
		const baseLang = lang?.split('-')[0]?.toLowerCase();
		if (baseLang && LanguageService.SUPPORTED_LANGS.includes(baseLang)) {
			return baseLang;
		}
		return LanguageService.DEFAULT_LANG;
	}

}